import { getClientes } from "@spark/services/clientes";
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { Card, CardBody } from '@spark/ui-components/components/Card';
import ClientesTable from './ClientesTable';
import ClientesFilter from './ClientesFilter';
import styled from 'styled-components';
import FilterPanel from '@spark/ui-components/components/FilterPanel';
import { filterConfig } from '../config/filterConfig';
import { ButtonWithLoading } from '@spark/ui-components/components/Button';
import ClientesHeader from './ClientesHeader';
import { notification } from '@spark/ui-components/utils/notification';
import { useAuth } from '@/context/AuthContext'; // Função para formatar CNPJ

const formatarCNPJ = cnpj => {
  if (!cnpj) return '-';
  const cnpjLimpo = cnpj.replace(/\D/g, '');
  return cnpjLimpo.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
};

const ClientesCard = () => {
  const history = useHistory();
  const {
    user
  } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [quantidadePorPagina, setQuantidadePorPagina] = useState(5);
  const isMountedRef = useRef(true); // Log do contexto de autenticação

  useEffect(() => {
    console.log('🔐 Contexto de autenticação:', {
      user
    });
  }, [user]);
  const [filtros, setFiltros] = useState({
    Nome: '',
    Cnpj: '',
    Telefone: ''
  }); // Cleanup quando componente é desmontado

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []); // filterConfig importado da configuração para seguir o pattern

  const handleFiltroChange = newFiltros => {
    setFiltros(newFiltros);
  };

  const handleLimparFiltros = clearedFiltros => {
    console.log('🧹 Limpar filtros clicado. Filtros limpos:', clearedFiltros);
    setFiltros(clearedFiltros);
    setLoading(true);
  };

  const fetchData = useCallback(async () => {
    if (!isMountedRef.current) return;

    try {
      console.log('🌐 Buscando clientes com filtros:', filtros);
      setLoading(true); // Preparar filtros para o serviço

      const filters = {
        quantidadePorPagina,
        pagina: paginaAtual,
        ...(filtros.Nome && {
          Nome: filtros.Nome
        }),
        ...(filtros.Cnpj && {
          Cnpj: filtros.Cnpj
        }),
        ...(filtros.Telefone && {
          Telefone: filtros.Telefone
        })
      };
      const responseData = await getClientes(filters);
      if (!isMountedRef.current) return;
      console.log('📥 Resposta da API:', responseData);
      console.log('📦 Registros:', responseData.registros);
      console.log('📦 Total de registros:', responseData.totalRegistros);
      console.log('📦 Página atual:', responseData.paginaAtual); // Garantir que temos um array de registros

      const registros = Array.isArray(responseData.registros) ? responseData.registros : [];
      console.log('📦 Registros processados:', registros);
      const dadosProcessados = registros.map(cliente => ({
        id: cliente.Id,
        nome: cliente.Nome ?? '-',
        cnpj: formatarCNPJ(cliente.CNPJ),
        telefone: cliente.Telefone ?? '-',
        email: cliente.Email ?? '-',
        observacoes: cliente.Observacao ?? '-',
        dataCriacao: cliente.DataCriacao ? new Date(cliente.DataCriacao).toLocaleDateString('pt-BR') : '-',
        dataModificacao: cliente.DataModificacao ? new Date(cliente.DataModificacao).toLocaleDateString('pt-BR') : '-'
      }));
      console.log('✅ Dados processados:', dadosProcessados);
      setData(dadosProcessados);
      setTotalRegistros(responseData.totalRegistros || dadosProcessados.length);
    } catch (error) {
      if (!isMountedRef.current) return;
      console.error('❌ Erro ao buscar dados:', error); // Erro tratado automaticamente pelo interceptor global do axios
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        setFilterLoading(false);
      }
    }
  }, [filtros, paginaAtual, quantidadePorPagina]); // Carregar dados iniciais

  useEffect(() => {
    console.log('🔄 useEffect fetchData chamado');
    console.log('🔍 Filtros atuais:', filtros);
    fetchData();
  }, [fetchData]); // Log quando dados mudam

  useEffect(() => {
    console.log('📊 Dados atualizados:', data);
    console.log('📊 Total de registros:', totalRegistros);
    console.log('📊 Loading:', loading);
  }, [data, totalRegistros, loading]); // Carregar dados quando filtros mudam

  useEffect(() => {
    console.log('🔍 Filtros mudaram, recarregando dados...');

    if (!loading) {
      fetchData();
    }
  }, [filtros]);
  const handleRowClick = useCallback(row => {
    console.log('🖱️ Clicou na linha:', row);
    console.log('🖱️ ID do cliente:', row.id);
    console.log('🖱️ Nome do cliente:', row.nome);

    if (row && row.id) {
      const url = `/pages/clientes/detalhes/${row.id}`;

      try {
        history.push(url);
      } catch (error) {
        console.error('❌ Erro na navegação:', error);
      }
    } else {
      console.error('❌ Dados da linha inválidos:', row);
    }
  }, [history]);
  const handleFilterApply = useCallback(newFilters => {
    console.log('🔍 Aplicando filtros:', newFilters);
    setFiltros(newFilters);
    setPaginaAtual(1);
    setFilterLoading(true);
  }, []);

  const handleAdicionar = () => {
    console.log('➕ Botão adicionar clicado');
    const url = '/pages/clientes/cadastro';
    console.log('➕ Navegando para:', url);
    console.log('➕ History object:', history);
    console.log('➕ History push function:', history.push);

    try {
      history.push(url);
      console.log('➕ Navegação executada com sucesso');
    } catch (error) {
      console.error('❌ Erro na navegação:', error);
    }
  };

  return <Card>
      <CardBody>
        <ClientesHeader totalRegistros={totalRegistros} onAdicionar={<ButtonWithLoading variant="primary" onClick={handleAdicionar}>
              Novo Cliente
            </ButtonWithLoading>} />

        

        <ClientesFilter loading={filterLoading} onApply={handleFilterApply} />

        <ClientesTable data={data} loading={loading} totalRegistros={totalRegistros} paginaAtual={paginaAtual} quantidadePorPagina={quantidadePorPagina} setPaginaAtual={setPaginaAtual} setQuantidadePorPagina={setQuantidadePorPagina} onRowClick={handleRowClick} />
        
      </CardBody>
    </Card>;
};

const HeaderWrap = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;
export default ClientesCard;
